import { Separator as SeparatorPrimitive } from "@base-ui/react/separator";
import type React from "react";
import { cn } from "../../lib/utils";

type SeparatorProps = SeparatorPrimitive.Props & {
  /**
   * Optional centered label ("or", "and", …) rendered between two rules.
   * Horizontal orientation only.
   */
  label?: string;
};

export function Separator({
  className,
  orientation = "horizontal",
  label,
  ...props
}: SeparatorProps): React.ReactElement {
  if (label != null && orientation === "horizontal") {
    // ONE separator, named by its label: the primitive keeps the role and takes
    // every caller prop, and the two hairlines are decoration inside it. Two
    // nested primitives would announce two unnamed dividers plus loose text.
    const rule = <div aria-hidden className="h-px min-w-0 flex-1 shrink bg-border" data-slot="separator-rule" />;
    return (
      <SeparatorPrimitive
        aria-label={label}
        className={cn("flex w-full shrink-0 items-center gap-3", className)}
        data-slot="separator-label"
        orientation="horizontal"
        {...props}
      >
        {rule}
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        {rule}
      </SeparatorPrimitive>
    );
  }
  return (
    <SeparatorPrimitive
      className={cn(
        // Both orientations default to filling their cross axis, but only
        // when the caller has not sized that axis: a variant-scoped default
        // outranks a caller's plain `w-7`/`h-5` in specificity, so each
        // default is guarded on no explicit class for its own axis.
        "shrink-0 bg-border data-[orientation=horizontal]:h-px data-[orientation=horizontal]:not-[[class^='w-']]:not-[[class*='_w-']]:w-full data-[orientation=vertical]:w-px data-[orientation=vertical]:not-[[class^='h-']]:not-[[class*='_h-']]:self-stretch",
        className,
      )}
      data-slot="separator"
      orientation={orientation}
      {...props}
    />
  );
}
